import { Image, Text } from '@chakra-ui/react';
import { useTranslation } from 'next-i18next';
import { useEffect, useMemo, useState } from 'react';

import type { ImageProps } from '@chakra-ui/react';

import { useShop } from '$src/hooks/queries';
import { useROBrandingPreviewAtom } from '$src/hooks/state/useBrandingPreviewAtom';

type Props = {
  /** Rendered logo height. Falls back to the shop name text if no logo. */
  h?: ImageProps['h'];
};

/**
 * Shop brand logo, with the same resolution logic the Header uses (S3 bucket
 * URL, branding-preview override, responsive srcSet from logoVariants) and a
 * shop-name text fallback when no logo is set or the image fails to load.
 */
export const BrandLogo = ({ h = '2rem' }: Props) => {
  const { t } = useTranslation('common');
  const { data: shop } = useShop();
  const brandingPreview = useROBrandingPreviewAtom();

  const urlLogoShop = useMemo(
    () =>
      shop?.logo?.includes('https://')
        ? shop?.logo
        : `https://${process.env.NEXT_PUBLIC_LOGOS_BUCKET}.s3.eu-west-3.amazonaws.com/${shop?.logo}`,
    [shop],
  );

  const previewLogoUrl = brandingPreview?.logoUrl;
  const effectiveLogoUrl = previewLogoUrl ?? urlLogoShop;
  const hasLogo = previewLogoUrl ? true : !!shop?.logo;
  const [logoLoadFailed, setLogoLoadedFailed] = useState(false);
  const [logoSrcSet, setLogoSrcSet] = useState('');

  useEffect(() => {
    if (!shop) return;
    if (shop.logoVariants?.length) {
      setLogoSrcSet(
        shop.logoVariants
          ?.map((variant) => `${shop.baseMediaUrl}${variant.fileKey} ${variant.name}`)
          .join(', ') ?? '',
      );
    }
  }, [shop]);

  if (!logoLoadFailed && hasLogo) {
    return (
      <Image
        h={h}
        maxH="100%"
        maxW="100%"
        objectFit="contain"
        alt={t('aria.shop_logo')}
        src={effectiveLogoUrl}
        srcSet={previewLogoUrl ? undefined : logoSrcSet}
        onError={() => setLogoLoadedFailed(true)}
      />
    );
  }

  return (
    <Text color="var(--text, #1C1C1C)" fontWeight={700} fontSize="1.5rem" noOfLines={1}>
      {shop?.name ?? ''}
    </Text>
  );
};
